import {
  Alert,
  Box,
  Button,
  ButtonDropdown,
  DatePicker,
  FormField,
  Grid,
  Header,
  KeyValuePairs,
  Link,
  Modal,
  Pagination,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
  Toggle,
} from '@cloudscape-design/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useDeferredValue, useState } from 'react';

import {
  createScorecardTrackingProfile,
  getScorecardTrackingProfileMeta,
  listScorecardTrackingProfiles,
  type ScorecardTrackingProfile,
  updateScorecardTrackingProfile,
} from '../../common/api/scorecardTracking';
import { getBusinessType } from '../../common/businessTypes';
import { BUSINESS_TYPES } from '../../common/types';
import type { Suggestion } from '../../common/useMaps';
import { formatScorecardPercentile } from '../../common/scorecardTracking';
import AutoCompleteSearch from '../../components/AutoCompleteSearch';
import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import { ScorecardGrade } from '../../components/ScorecardIdentity';
import { StarsIcon } from '../../components/StarsIcon';
import { ScorecardSourceLabel } from '../../common/scorecardSources';

const ACTIVE_STATUSES = new Set(['initializing', 'backfilling', 'refreshing']);
const EXPECTED_SOURCES: Record<string, string[]> = {
  Automotive: ['Google', 'Yelp', 'BBB', 'DealerRater', 'Cars.com', 'CARFAX', 'Edmunds', 'CarGurus'],
  Restaurants: ['Google', 'Yelp', 'BBB', 'OpenTable', 'TripAdvisor'],
  Hotel: ['Google', 'Yelp', 'BBB', 'TripAdvisor', 'Booking.com', 'Expedia'],
};

function likelySources(businessType: string): string {
  return (EXPECTED_SOURCES[businessType] ?? ['Google', 'Yelp', 'BBB']).join(', ');
}

function statusType(status: string): 'success' | 'error' | 'warning' | 'in-progress' | 'stopped' {
  if (status === 'ready') return 'success';
  if (status === 'ready_partial') return 'warning';
  if (status === 'failed') return 'error';
  if (status === 'archived') return 'stopped';
  return 'in-progress';
}

export function trackingStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    initializing: 'Queued',
    backfilling: 'Building history',
    refreshing: 'Refreshing',
    ready: 'Ready',
    ready_partial: 'Ready — partial',
    failed: 'Needs attention',
    archived: 'Archived',
  };
  return labels[status] ?? status;
}

function formatDate(value?: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

function latestGoogleMetric(profile: ScorecardTrackingProfile) {
  return (profile.latest_snapshot?.metrics ?? []).find(
    (item) => item.source === 'google' && item.window_days === 30,
  );
}

export default function ScorecardWatchlistPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedPlaces, setSelectedPlaces] = useState<Suggestion[]>([]);
  const [ongoingEnabled, setOngoingEnabled] = useState(true);
  const [businessType, setBusinessType] = useState('Other');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ongoingFilter, setOngoingFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [updatedAfter, setUpdatedAfter] = useState('');
  const [updatedBefore, setUpdatedBefore] = useState('');
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortDesc, setSortDesc] = useState(true);
  const [pauseTarget, setPauseTarget] = useState<ScorecardTrackingProfile | null>(null);

  const metaQuery = useQuery({
    queryKey: ['scorecard-watchlist-meta'],
    queryFn: getScorecardTrackingProfileMeta,
  });

  const profilesQuery = useQuery({
    queryKey: [
      'scorecard-watchlist',
      page,
      deferredSearch,
      statusFilter,
      ongoingFilter,
      sourceFilter,
      updatedAfter,
      updatedBefore,
      sortBy,
      sortDesc,
    ],
    queryFn: () =>
      listScorecardTrackingProfiles({
        page,
        limit: 20,
        search: deferredSearch || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        ongoingEnabled: ongoingFilter === 'all' ? undefined : ongoingFilter === 'on',
        source: sourceFilter === 'all' ? undefined : sourceFilter,
        includeArchived: statusFilter === 'archived',
        updatedAfter: updatedAfter ? `${updatedAfter}T00:00:00` : undefined,
        updatedBefore: updatedBefore ? `${updatedBefore}T23:59:59` : undefined,
        sortBy,
        sortDesc,
      }),
    refetchInterval: (query) =>
      query.state.data?.items.some((item) => ACTIVE_STATUSES.has(item.status)) ? 5_000 : false,
  });

  const createMutation = useMutation({
    mutationFn: createScorecardTrackingProfile,
    onSuccess: async (profile) => {
      await queryClient.invalidateQueries({ queryKey: ['scorecard-watchlist'] });
      setCreateOpen(false);
      setSelectedPlaces([]);
      setBusinessType('Other');
      navigate({
        to: '/dashboard/scorecard/watchlist/$uuid',
        params: { uuid: profile.uuid },
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ uuid, enabled }: { uuid: string; enabled: boolean }) =>
      updateScorecardTrackingProfile(uuid, { ongoing_enabled: enabled }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['scorecard-watchlist'] });
    },
  });

  const items = profilesQuery.data?.items ?? [];
  const pagesCount = Math.max(1, profilesQuery.data?.pages ?? 1);
  const selectedPlace = selectedPlaces[0];
  const statusOptions = [
    { value: 'all', label: 'All active states' },
    ...(metaQuery.data?.statuses ?? []).map((item) => ({
      value: item,
      label: trackingStatusLabel(item),
    })),
  ];
  const sourceOptions = [
    { value: 'all', label: 'All sources' },
    ...(metaQuery.data?.sources ?? []).map((item) => ({ value: item, label: item })),
  ];

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Scorecards', href: '/dashboard/scorecard' },
        { text: 'Watchlist', href: '' },
      ]}
      contentType="table"
    >
      <Modal
        visible={createOpen}
        onDismiss={() => !createMutation.isPending && setCreateOpen(false)}
        header="Add a business to the Scorecard Watchlist"
        size="large"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setCreateOpen(false)} disabled={createMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={createMutation.isPending}
                disabled={!selectedPlace}
                onClick={() => {
                  if (!selectedPlace) return;
                  createMutation.mutate({
                    google_place_id: selectedPlace.placeId,
                    business_type: businessType,
                    ongoing_enabled: ongoingEnabled,
                  });
                }}
              >
                Confirm and start tracking
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          <Alert type="info" header="The first run builds a year of comparable history">
            We request 545 days of source reviews so the page can calculate twelve historical
            monthly points with complete 180-day windows. The initial run may take substantially
            longer than a normal scorecard.
          </Alert>
          <AutoCompleteSearch
            selectedItems={selectedPlaces}
            setSelectedItems={(next) => {
              const selected = next.slice(-1);
              setSelectedPlaces(selected);
              setBusinessType(getBusinessType(selected[0]?.tags ?? []) ?? 'Other');
            }}
            loading={createMutation.isPending}
            placeholder="Search for a business on Google Places"
          />
          {selectedPlace ? (
            <KeyValuePairs
              columns={2}
              items={[
                { label: 'Business', value: selectedPlace.label },
                { label: 'Address', value: selectedPlace.description },
              ]}
            />
          ) : null}
          <FormField
            label="Business type"
            description="Confirm or override the inferred type; it controls which review sources are attempted."
          >
            <Select
              selectedOption={
                BUSINESS_TYPES.find((option) => option.value === businessType) ?? {
                  label: 'Other',
                  value: 'Other',
                }
              }
              options={BUSINESS_TYPES}
              onChange={({ detail }) => setBusinessType(detail.selectedOption.value ?? 'Other')}
              disabled={createMutation.isPending}
            />
          </FormField>
          <Alert type="info" header="Expected review sources">
            {likelySources(businessType)}. Final availability depends on trusted source matching and
            what each platform makes available for this business.
          </Alert>
          <Toggle
            checked={ongoingEnabled}
            onChange={({ detail }) => setOngoingEnabled(detail.checked)}
            disabled={createMutation.isPending}
          >
            Continue refreshing every 30 days after the initial history is ready
          </Toggle>
          <Box color="text-body-secondary">
            After the first usable snapshot, a private-by-link page is published at{' '}
            <code>/h/business-name-address</code>. It can be disabled at any time.
          </Box>
          {createMutation.isError ? (
            <Alert type="error" header="Could not add this business">
              {createMutation.error.message}
            </Alert>
          ) : null}
        </SpaceBetween>
      </Modal>

      <Modal
        visible={Boolean(pauseTarget)}
        onDismiss={() => !updateMutation.isPending && setPauseTarget(null)}
        header="Pause monthly Scorecard tracking?"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setPauseTarget(null)} disabled={updateMutation.isPending}>
                Keep tracking
              </Button>
              <Button
                variant="primary"
                loading={updateMutation.isPending}
                onClick={() => {
                  if (!pauseTarget) return;
                  updateMutation.mutate(
                    { uuid: pauseTarget.uuid, enabled: false },
                    { onSuccess: () => setPauseTarget(null) },
                  );
                }}
              >
                Pause tracking
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        Existing snapshots and the public history remain available. No new 30-day run will be
        scheduled until tracking is resumed.
      </Modal>

      <Table
        loading={profilesQuery.isLoading}
        loadingText="Loading Scorecard Watchlist"
        items={items}
        trackBy="uuid"
        stickyHeader
        wrapLines
        header={
          <Header
            variant="h1"
            counter={profilesQuery.data ? `(${profilesQuery.data.total})` : undefined}
            description="Businesses with longitudinal scorecards and an optional 30-day refresh cadence."
            actions={
              <Button variant="primary" iconName="add-plus" onClick={() => setCreateOpen(true)}>
                Add business
              </Button>
            }
          >
            Scorecard Watchlist
          </Header>
        }
        filter={
          <SpaceBetween size="s">
            <TextFilter
              filteringText={search}
              filteringPlaceholder="Find by business, address, or Google Place ID"
              filteringAriaLabel="Filter Scorecard Watchlist"
              onChange={({ detail }) => {
                setSearch(detail.filteringText);
                setPage(1);
              }}
              countText={`${profilesQuery.data?.total ?? 0} businesses`}
            />
            <Grid
              gridDefinition={[
                { colspan: { default: 12, s: 3 } },
                { colspan: { default: 12, s: 3 } },
                { colspan: { default: 12, s: 3 } },
                { colspan: { default: 12, s: 3 } },
              ]}
            >
              <FormField label="Tracking state">
                <Select
                  selectedOption={statusOptions.find((item) => item.value === statusFilter) ?? statusOptions[0]}
                  options={statusOptions}
                  onChange={({ detail }) => {
                    setStatusFilter(detail.selectedOption.value ?? 'all');
                    setPage(1);
                  }}
                />
              </FormField>
              <FormField label="Monthly tracking">
                <Select
                  selectedOption={
                    [
                      { value: 'all', label: 'On or paused' },
                      { value: 'on', label: 'On' },
                      { value: 'off', label: 'Paused' },
                    ].find((item) => item.value === ongoingFilter) ?? null
                  }
                  options={[
                    { value: 'all', label: 'On or paused' },
                    { value: 'on', label: 'On' },
                    { value: 'off', label: 'Paused' },
                  ]}
                  onChange={({ detail }) => {
                    setOngoingFilter(detail.selectedOption.value ?? 'all');
                    setPage(1);
                  }}
                />
              </FormField>
              <FormField label="Review source">
                <Select
                  selectedOption={sourceOptions.find((item) => item.value === sourceFilter) ?? sourceOptions[0]}
                  options={sourceOptions}
                  onChange={({ detail }) => {
                    setSourceFilter(detail.selectedOption.value ?? 'all');
                    setPage(1);
                  }}
                />
              </FormField>
              <FormField label="Sort">
                <Select
                  selectedOption={{
                    value: `${sortBy}:${sortDesc ? 'desc' : 'asc'}`,
                    label:
                      sortBy === 'name'
                        ? 'Business A–Z'
                        : sortBy === 'next_run_at'
                          ? 'Next run soonest'
                          : 'Recently updated',
                  }}
                  options={[
                    { value: 'updated_at:desc', label: 'Recently updated' },
                    { value: 'name:asc', label: 'Business A–Z' },
                    { value: 'next_run_at:asc', label: 'Next run soonest' },
                  ]}
                  onChange={({ detail }) => {
                    const [field, direction] = (detail.selectedOption.value ?? 'updated_at:desc').split(':');
                    setSortBy(field);
                    setSortDesc(direction === 'desc');
                    setPage(1);
                  }}
                />
              </FormField>
            </Grid>
            <Grid
              gridDefinition={[
                { colspan: { default: 12, s: 3 } },
                { colspan: { default: 12, s: 3 } },
              ]}
            >
              <FormField label="Updated on or after">
                <DatePicker
                  value={updatedAfter}
                  onChange={({ detail }) => {
                    setUpdatedAfter(detail.value);
                    setPage(1);
                  }}
                  placeholder="YYYY/MM/DD"
                />
              </FormField>
              <FormField label="Updated on or before">
                <DatePicker
                  value={updatedBefore}
                  onChange={({ detail }) => {
                    setUpdatedBefore(detail.value);
                    setPage(1);
                  }}
                  placeholder="YYYY/MM/DD"
                />
              </FormField>
            </Grid>
          </SpaceBetween>
        }
        pagination={
          <Pagination
            currentPageIndex={Math.min(page, pagesCount)}
            pagesCount={pagesCount}
            onChange={({ detail }) => setPage(detail.currentPageIndex)}
          />
        }
        empty={
          <Box textAlign="center" color="inherit">
            <SpaceBetween size="s">
              <b>No businesses are on the Watchlist.</b>
              <Box color="inherit">Add one to build its first Scorecard History.</Box>
              <Button onClick={() => setCreateOpen(true)}>Add business</Button>
            </SpaceBetween>
          </Box>
        }
        columnDefinitions={[
          {
            id: 'business',
            header: 'Business',
            cell: (item) => (
              <SpaceBetween size="xxs">
                <Link
                  href={`/dashboard/scorecard/watchlist/${item.uuid}`}
                  onFollow={(event) => {
                    event.preventDefault();
                    navigate({
                      to: '/dashboard/scorecard/watchlist/$uuid',
                      params: { uuid: item.uuid },
                    });
                  }}
                >
                  {item.place?.name ?? 'Resolving Google Place…'}
                </Link>
                <Box color="text-body-secondary" fontSize="body-s">
                  {item.place?.address ?? item.google_place_id}
                </Box>
                {item.place?.rating == null ? null : (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Box variant="span">{item.place.rating.toFixed(1)}</Box>
                    <StarsIcon stars={item.place.rating} />
                    <Link
                      href={item.place ? `/dashboard/places/${item.place.uuid}` : undefined}
                      variant="secondary"
                    >
                      {item.place.total_review_count?.toLocaleString() ?? '—'} Google reviews
                    </Link>
                  </SpaceBetween>
                )}
              </SpaceBetween>
            ),
            minWidth: 300,
          },
          {
            id: 'scorecard',
            header: 'Latest scorecard',
            minWidth: 280,
            cell: (item) => {
              const metric = latestGoogleMetric(item);
              const sources = [
                ...new Set((item.latest_snapshot?.metrics ?? []).map((candidate) => candidate.source)),
              ];
              return (
                <SpaceBetween size="xxs">
                  <div className="flex flex-wrap items-center gap-3">
                    <ScorecardGrade
                      grade={metric?.composite_grade}
                      percentile={metric?.composite_percentile}
                      compact
                    />
                    <SpaceBetween size="xxxs">
                      <Box fontWeight="bold">Past 30 days</Box>
                      <Box color="text-body-secondary">
                        {metric?.composite_percentile == null
                          ? item.active_run
                            ? 'Calculating score'
                            : 'Score needs rebuilding'
                          : `${formatScorecardPercentile(metric.composite_percentile)} percentile`}
                      </Box>
                    </SpaceBetween>
                  </div>
                  {sources.length ? (
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                      {sources.map((sourceName) => (
                        <ScorecardSourceLabel key={sourceName} source={sourceName} />
                      ))}
                    </div>
                  ) : (
                    <StatusIndicator type="in-progress">Building first scorecard</StatusIndicator>
                  )}
                  {item.latest_scorecard_slug ? (
                    <Link href={`/${item.latest_scorecard_slug}`} external>
                      Open point-in-time scorecard
                    </Link>
                  ) : null}
                </SpaceBetween>
              );
            },
          },
          {
            id: 'tracking',
            header: 'Tracking schedule',
            minWidth: 300,
            cell: (item) => (
              <SpaceBetween size="xxxs">
                <StatusIndicator type={statusType(item.status)}>
                  {trackingStatusLabel(item.status)}
                </StatusIndicator>
                <Box color="text-body-secondary">
                  Last successful: {formatDate(item.last_successful_run_at)}
                </Box>
                <Box color="text-body-secondary">
                  Next: {item.ongoing_enabled ? formatDate(item.next_run_at) : 'Paused'}
                </Box>
                <Toggle
                  checked={item.ongoing_enabled}
                  disabled={
                    item.status === 'archived' ||
                    (updateMutation.isPending && updateMutation.variables?.uuid === item.uuid)
                  }
                  onChange={({ detail }) =>
                    detail.checked
                      ? updateMutation.mutate({ uuid: item.uuid, enabled: true })
                      : setPauseTarget(item)
                  }
                >
                  {item.ongoing_enabled ? 'Monthly tracking on' : 'Monthly tracking paused'}
                </Toggle>
              </SpaceBetween>
            ),
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (item) => {
              const publicReady = Boolean(
                item.public_slug && item.public_enabled && item.published_at,
              );
              return (
                <ButtonDropdown
                  items={[
                    { id: 'details', text: 'Tracking details' },
                    ...(item.place ? [{ id: 'place', text: 'Open place' }] : []),
                    ...(publicReady ? [{ id: 'public', text: 'Open public history' }] : []),
                    ...(publicReady ? [{ id: 'copy', text: 'Copy public link' }] : []),
                    ...(item.latest_scorecard_slug
                      ? [{ id: 'scorecard', text: 'Open latest scorecard' }]
                      : []),
                  ]}
                  onItemClick={({ detail }) => {
                    if (detail.id === 'details') {
                      navigate({
                        to: '/dashboard/scorecard/watchlist/$uuid',
                        params: { uuid: item.uuid },
                      });
                    } else if (detail.id === 'place' && item.place) {
                      navigate({
                        to: '/dashboard/places/$uuid',
                        params: { uuid: item.place.uuid },
                      });
                    } else if (detail.id === 'public' && item.public_slug) {
                      window.open(`/h/${item.public_slug}`, '_blank', 'noopener,noreferrer');
                    } else if (detail.id === 'copy' && item.public_slug) {
                      void navigator.clipboard.writeText(
                        `${window.location.origin}/h/${item.public_slug}`,
                      );
                    } else if (detail.id === 'scorecard' && item.latest_scorecard_slug) {
                      window.open(
                        `/${item.latest_scorecard_slug}`,
                        '_blank',
                        'noopener,noreferrer',
                      );
                    }
                  }}
                >
                  More
                </ButtonDropdown>
              );
            },
            width: 130,
          },
        ]}
      />
      {profilesQuery.error ? (
        <Alert type="error" header="Could not load the Watchlist">
          {profilesQuery.error.message}
        </Alert>
      ) : null}
    </DashboardViewLayout>
  );
}
